import { SettingsPermissions } from "@redotech/account-common/permissions";
import { t } from "@redotech/redo-i18n";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import {
  RETURNS_AUTOMATIONS_PROCESSING_SEGMENT,
  RETURNS_AUTOMATIONS_SETTINGS_PATH,
  RETURNS_COMPENSATION_FLOW_EDITOR_SEGMENT,
  RETURNS_CONSOLIDATED_SETTINGS_PATH,
  RETURNS_RESTOCKING_FLOW_EDITOR_SEGMENT,
} from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import {
  catalogRoutePermission,
  INTEGRATIONS_HUB_PERMISSIONS,
} from "../../../../integrations/catalog/catalog-data";
import {
  PRODUCT_EXCHANGE_GUIDANCE_PATH,
  PRODUCTS_AS_VARIANTS_PATH,
  VARIANT_EXCHANGE_GUIDANCE_PATH,
} from "../../../../setting/returns/portal/portal-links";
import { ReturnsSettingsParentBreadcrumb } from "../../../../setting/returns/returns-settings-parent-breadcrumb";

export const returnSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      get breadcrumb() {
        return t("Returns");
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: RETURNS_CONSOLIDATED_SETTINGS_PATH,
    lazy: async () => {
      const { ReturnsSettingsPage } =
        await import("../../../../setting/returns/returns-settings-page");
      return { Component: ReturnsSettingsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Assessments", {
          context: "merchant settings label for return assessments",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "assessments",
    lazy: async () => {
      const { AssessmentsWithTabsPage } =
        await import("../../../../setting/assessments/assessments-with-tabs");
      return { Component: AssessmentsWithTabsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Automations", {
          context: "merchant settings label for return automations",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    children: [
      {
        path: "",
        lazy: async () => {
          const { AutomationsPage } =
            await import("../../../../setting/automation");
          return { Component: AutomationsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Processing", {
              context: "merchant settings label for return processing flows",
            });
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: RETURNS_AUTOMATIONS_PROCESSING_SEGMENT,
        children: [
          {
            index: true,
            lazy: async () => {
              const { ProcessingPage } =
                await import("../../../../setting/automation/processing/processing-page");
              return { Component: ProcessingPage };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Compensation flow");
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: RETURNS_COMPENSATION_FLOW_EDITOR_SEGMENT,
            lazy: async () => {
              const { CompensationFlowEditor } =
                await import("../../../../setting/flow-editor/compensation-flow/compensation-flow-editor");
              return { Component: CompensationFlowEditor };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Restocking flow");
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: RETURNS_RESTOCKING_FLOW_EDITOR_SEGMENT,
            lazy: async () => {
              const { RestockingFlowEditor } =
                await import("../../../../setting/flow-editor/restocking-flow/restocking-flow-editor");
              return { Component: RestockingFlowEditor };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Return processing flows");
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: ":flowId",
            lazy: async () => {
              const { ReturnProcessingFlowBuilder } =
                await import("../../../../setting/return-processing-flows/return-processing-flow-builder");
              return { Component: ReturnProcessingFlowBuilder };
            },
          },
        ],
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Consolidation", {
          context: "merchant settings label for return consolidation",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "consolidation",
    lazy: async () => {
      const { ConsolidationSettingsPage } =
        // redont-allow-dynamic-import: route-level code splitting
        await import("@redotech/redo-merchant-fulfillment-app/setting/consolidation");
      return { Component: ConsolidationSettingsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Coverage", {
          context: "merchant settings label for return coverage",
        });
      },
      breadcrumbNavigate: false,
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "coverage",
    element: <ReturnsSettingsParentBreadcrumb legacyPath="coverage" />,
    children: [
      {
        index: true,
        lazy: async () => {
          const { CoveragePage: CoverageSettings } =
            await import("../../../../setting/coverage/coverage");
          return { Component: CoverageSettings };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Coverage Builder", {
              context:
                "merchant settings breadcrumb for the return coverage builder",
            });
          },
        },
        path: "coverage-builder",
        lazy: async () => {
          const { CoverageBuilderPage } =
            await import("../../../../setting/coverage/coverage-builder/coverage-builder");
          return { Component: CoverageBuilderPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Custom Logo");
          },
        },
        path: "custom-logo",
        lazy: async () => {
          const { CustomLogoPage } =
            await import("../../../../setting/coverage/custom-logo");
          return { Component: CustomLogoPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Info Modal", {
              context:
                "merchant settings breadcrumb for return coverage information modal",
            });
          },
        },
        path: "info-modal",
        lazy: async () => {
          const { InfoModalPage } =
            await import("../../../../setting/coverage/info-modal");
          return { Component: InfoModalPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Exchanges", {
          context: "merchant settings label for return exchanges",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "exchanges",
    lazy: async () => {
      const { ExchangesPage: Exchanges } =
        await import("../../../../setting/exchanges");
      return { Component: Exchanges };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Integrations", {
          context: "merchant settings label for the integrations catalog",
        });
      },
      permission: [...INTEGRATIONS_HUB_PERMISSIONS],
    },
    path: "integrations",
    children: [
      {
        path: "",
        lazy: async () => {
          const { UnifiedIntegrationsPage } =
            await import("../../../../integrations/pages/unified-integrations-page");
          return { Component: UnifiedIntegrationsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Cordial",
          permission: catalogRoutePermission("cordial"),
        },
        path: "cordial",
        lazy: async () => {
          const { CordialIntegrationSettingsPage } =
            await import("../../../../setting/cordial-integration-settings");
          return { Component: CordialIntegrationSettingsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Integration");
          },
          permission: [...INTEGRATIONS_HUB_PERMISSIONS],
        },
        path: "vendor/:slug",
        lazy: async () => {
          const { IntegrationVendorPage } =
            await import("../../../../integrations/pages/integration-vendor-page");
          return { Component: IntegrationVendorPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Merchant-created returns", {
          context:
            "merchant settings label for merchant-created return permissions",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "permissions",
    lazy: async () => {
      const { PermissionsPage } =
        await import("../../../../setting/permissions/permissions");
      return { Component: PermissionsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Point of sale", {
          context: "merchant settings label for point-of-sale returns",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "pos",
    children: [
      {
        path: "",
        lazy: async () => {
          const { PointOfSalePage } =
            await import("../../../../setting/point-of-sale/point-of-sale-page");
          return { Component: PointOfSalePage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Integration locations");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "integration-locations",
        lazy: async () => {
          const { IntegrationLocationsPage } =
            await import("../../../../setting/point-of-sale/integration-locations-page");
          return { Component: IntegrationLocationsPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Post processing", {
          context: "merchant settings label for post-return processing",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "post-processing",
    lazy: async () => {
      const { PostProcessingPage } =
        await import("@redotech/redo-merchant-fulfillment-app/setting/post-processing");
      return { Component: PostProcessingPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Processing groups", {
          context: "merchant settings label for return processing groups",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "processing-groups",
    children: [
      {
        path: "",
        lazy: async () => {
          const { ProcessingGroupsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/processing-groups");
          return { Component: ProcessingGroupsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Group details");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: ":groupId",
        lazy: async () => {
          const { ProcessingGroupDetailPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/processing-group-detail");
          return { Component: ProcessingGroupDetailPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Refunds & store credit", {
          context:
            "merchant settings label for return refunds and store credit",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "processing",
    lazy: async () => {
      const { ProcessingPage } = await import("../../../../setting/processing");
      return { Component: ProcessingPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Products as Variants");
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: PRODUCTS_AS_VARIANTS_PATH,
    lazy: async () => {
      const { ProductsAsVariantsPage } =
        await import("../../../../setting/returns/portal/ai-exchange-agent/redo-only-pages");
      return { Component: ProductsAsVariantsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Variant {{productName}} Guidance", {
          productName: "Exchange AI",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: VARIANT_EXCHANGE_GUIDANCE_PATH,
    lazy: async () => {
      const { VariantExchangeGuidancePage } =
        await import("../../../../setting/returns/portal/ai-exchange-agent/redo-only-pages");
      return { Component: VariantExchangeGuidancePage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Product {{productName}} Guidance", {
          productName: "Exchange AI",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: PRODUCT_EXCHANGE_GUIDANCE_PATH,
    lazy: async () => {
      const { ProductExchangeGuidancePage } =
        await import("../../../../setting/returns/portal/ai-exchange-agent/redo-only-pages");
      return { Component: ProductExchangeGuidancePage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Return detail view", {
          context: "merchant settings label for the return detail view",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "returns-ui",
    lazy: async () => {
      const { ReturnsUiPage } =
        await import("../../../../setting/returns-ui/returns-ui");
      return { element: <ReturnsUiPage /> };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Shipping", {
          context: "merchant settings label for return shipping",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "shipping",
    element: <ReturnsSettingsParentBreadcrumb legacyPath="shipping" />,
    children: [
      {
        path: "",
        lazy: async () => {
          const { ShippingPage } =
            await import("../../../../setting/shipping/shipping-page");
          return { Component: ShippingPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Label flow");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "labels-flow-editor",
        lazy: async () => {
          const { LabelsFlowEditor } =
            await import("../../../../setting/flows/labels-flow-editor");
          return { Component: LabelsFlowEditor };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Location flow");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "location-flow-editor",
        lazy: async () => {
          const { LocationFlowEditor } =
            await import("../../../../setting/flows/location-flow-editor");
          return { Component: LocationFlowEditor };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Warehouse", {
          context: "merchant settings label for the return warehouse",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "warehouse",
    element: <ReturnsSettingsParentBreadcrumb legacyPath="warehouse" />,
    children: [
      {
        path: "",
        lazy: async () => {
          const { ReturnsCentersIndexPage } =
            await import("../../../../setting/managed-returns/returns-centers-index");
          return { Component: ReturnsCentersIndexPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Integration");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "integrations/:provider",
        lazy: async () => {
          const { IntegrationDetailPage } =
            await import("../../../../setting/managed-returns/integration-detail");
          return { Component: IntegrationDetailPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Photo guidance");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "photo-guidance",
        lazy: async () => {
          const { PhotoInstructionsPage } =
            // redont-allow-dynamic-import: route-level code splitting
            await import("@redotech/redo-merchant-fulfillment-app/setting/photo-instructions/photo-instructions-page");
          return { Component: PhotoInstructionsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Processing flow");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "processing-flow",
        lazy: async () => {
          const { WarehouseProcessingFlowEditor } =
            await import("../../../../setting/flows/warehouse-processing-flow-editor");
          return { element: <WarehouseProcessingFlowEditor /> };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Product transform");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "product-transform",
        lazy: async () => {
          const { ProductTransformPage } =
            await import("../../../../setting/product/product-transform");
          return { Component: ProductTransformPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Withdrawals", {
          context:
            "merchant settings label for customer statutory withdrawal-from-purchase requests, equivalent to the legal right of withdrawal, not cash or inventory withdrawals",
        });
      },
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      ],
    },
    path: "withdrawals",
    lazy: async () => {
      const { WithdrawalsPage } =
        await import("@redotech/redo-merchant-general-app/setting/withdrawals/withdrawals-page");
      return { Component: WithdrawalsPage };
    },
  },
];
